import { Button, ButtonProps } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useUser } from "@/hooks/useUserProvider";
import { request } from "@/lib/request";
import { cn } from "@/lib/utils";
import { ChevronRightIcon, InfoIcon, Loader2Icon } from "lucide-react";
import { useState } from "react";
import { captureEvent, sanitizeErrorReason } from "@/lib/analytics";

export const GitHubPersonal = ({
  children,
  size,
  variant,
}: {
  children: React.ReactNode;
  size: ButtonProps["size"];
  variant: ButtonProps["variant"];
}) => {
  const { syncUser } = useUser();

  const [open, setOpen] = useState(false);
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!token) {
      setError("Please provide your GitHubPersonal API token.");
      return;
    }

    setError("");
    setLoading(true);
    captureEvent("onboarding_connect_started", {
      service: "github_personal",
      flow: "token",
    });

    try {
      await request
        .post(`/auth/github-personal`, {
          apiToken: token,
        })
        .catch(err => {
          throw new Error(
            err.response?.data.message
              ? err.response?.data.message
              : "Something went wrong, please try again.",
          );
        });

      await syncUser();
      captureEvent("onboarding_connect_succeeded", {
        service: "github_personal",
        flow: "token",
      });
      setOpen(false);
    } catch (err) {
      captureEvent("onboarding_connect_failed", {
        service: "github_personal",
        flow: "token",
        reason: sanitizeErrorReason(err),
      });
      setError(
        err instanceof Error
          ? err.message
          : "Something went wrong, please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant={variant} size={size}>
          {children}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Connect GitHub Personal Token</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <p className="text-sm text-muted-foreground">
            In order to connect and view your personal GitHub info on UniDeck,
            we need your Personal Access Token (PAT) provided in your GitHub
            profile's settings page.
          </p>
          <div className="mt-4 flex items-center justify-center gap-2 text-sm text-primary/80">
            <a
              href="https://github.com/settings/tokens/new?description=UniDeck&scopes=repo,notifications"
              target="_blank"
              className="text-primary underline"
            >
              GitHub PAT Settings
            </a>
            <ChevronRightIcon className="size-4 text-primary/30" />
            <span>
              Personal Access Token
              <Tooltip>
                <TooltipTrigger>
                  <InfoIcon className="ms-1 size-4" />
                </TooltipTrigger>
                <TooltipContent className="flex max-w-[300px] flex-col gap-2 bg-secondary text-white">
                  <p>
                    Please note GitHub does not provide an automatic way to
                    connect to their notification system, so we need a Personal
                    Access Token to connect to your account and show your
                    notifications.
                  </p>
                  <p>
                    If you feel unsafe at any point, please reset the PAT key in
                    your GitHub profile's settings page.
                  </p>
                </TooltipContent>
              </Tooltip>
            </span>
            <ChevronRightIcon className="size-4 text-primary/30" />
            <span>Reveal and Copy</span>
          </div>
          <Input
            className={cn({
              "border-red-500": error,
            })}
            placeholder="Paste your Personal Access Token (PAT) here"
            value={token}
            onChange={e => setToken(e.target.value)}
          />
          {error ? (
            <p className="-mt-2 text-sm text-red-500">
              {error || "Something went wrong, please try again."}
            </p>
          ) : null}
          <div className="flex justify-end gap-2">
            <DialogClose>
              <Button type="button" variant="secondary">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" variant="default" disabled={loading}>
              {loading ? (
                <>
                  <Loader2Icon className="animate-spin" />
                  Connecting
                </>
              ) : (
                <>Connect</>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
